import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, downloadAsBlob } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Cpu, Package, FileDown, Loader2, Eye, EyeOff,
} from "lucide-react";

/**
 * Iter104: Extracted from Settings.jsx (was 232 lines inline).
 * RFID + Kiosk Pi setup section — Pi Imager 1-click install, manual SCP install,
 * kiosk previews, production reader token reveal + copy.
 * Access: admin / secretary / prodekan (parent gates rendering).
 */
export default function RfidPiSetupCard() {
  const [rfidFiles, setRfidFiles] = useState([]);
  const [rfidBusy, setRfidBusy] = useState(false);
  const [readerToken, setReaderToken] = useState("");
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    api.get("/admin/rfid-pi/files")
      .then(r => setRfidFiles(r.data.files || []))
      .catch(() => {});
    api.get("/admin/rfid-pi/reader-token")
      .then(r => setReaderToken(r.data.token || ""))
      .catch(() => {});
  }, []);

  const downloadRfidBundle = async () => {
    setRfidBusy(true);
    try {
      await downloadAsBlob("/admin/rfid-pi/bundle", "kif-rfid-pi.tar.gz");
      toast.success("Bundle preuzet — kopiraj na Raspberry Pi i raspakiraj");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri preuzimanju");
    } finally { setRfidBusy(false); }
  };

  const downloadRfidFile = async (name) => {
    try {
      await downloadAsBlob(`/admin/rfid-pi/file/${encodeURIComponent(name)}`, name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri preuzimanju");
    }
  };

  const prodSnippet = readerToken
    ? `KIF_READER_TOKEN='${readerToken}' curl -sL ${window.location.origin}/api/rfid-pi/bootstrap.sh | sudo -E bash`
    : "Učitavanje produkcijskog tokena…";
  const plainSnippet = `curl -sL ${window.location.origin}/api/rfid-pi/bootstrap.sh | sudo bash`;

  return (
    <Card data-testid="rfid-pi-card" className="border-sky-400/30 bg-sky-50/30 dark:bg-sky-950/10">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Cpu className="w-4 h-4"/>RFID čitač + Kiosk display (Raspberry Pi)
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Postavi fizički RFID čitač s fullscreen kiosk prikazom (KIF logo + ime zaposlenika + vrijeme prijave/odjave).
          Auto-install za prvi boot, ili manualna SCP instalacija.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Kiosk Preview Thumbnails */}
        <div className="rounded-md border bg-background/60 p-3 space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Kako će izgledati ekran na Pi-u
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="kiosk-preview-grid">
            {[
              { name: "kiosk_idle.png", label: "Idle (čeka karticu)" },
              { name: "kiosk_prijava.png", label: "Prijava (in)" },
              { name: "kiosk_odjava.png", label: "Odjava (out)" },
              { name: "kiosk_error.png", label: "Greška / nepoznata" },
            ].map(p => (
              <div key={p.name} className="space-y-1" data-testid={`kiosk-preview-${p.name}`}>
                <img
                  src={`/api/rfid-pi/preview/${p.name}`}
                  alt={p.label}
                  className="rounded border w-full aspect-video object-cover"
                />
                <p className="text-[11px] text-center text-muted-foreground">{p.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* OPTION A: One-click first-boot install via Pi Imager */}
        <details className="rounded-md border bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-400/40 p-3" open data-testid="rfid-pi-imager-option">
          <summary className="cursor-pointer font-semibold text-sm flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-700 dark:text-emerald-300"/>
            Opcija A: Raspberry Pi Imager — 1-klik instalacija (preporučeno)
          </summary>
          <div className="mt-3 space-y-3 text-xs">
            <p className="text-muted-foreground">
              Preuzmi službeni <strong className="text-foreground">Raspberry Pi Imager</strong> s <a className="underline" href="https://www.raspberrypi.com/software/" target="_blank" rel="noreferrer">raspberrypi.com/software</a>.
              Zatim:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Otvori Imager → <strong>&quot;Choose Device&quot;</strong> → odaberi svoj Pi model</li>
              <li><strong>&quot;Choose OS&quot;</strong> → <em>Raspberry Pi OS Lite (64-bit)</em></li>
              <li><strong>&quot;Choose Storage&quot;</strong> → odaberi SD karticu</li>
              <li>Klikni <strong>&quot;Next&quot;</strong> → <strong>&quot;Edit Settings&quot;</strong> (⚙️ Advanced Options)</li>
              <li>U <strong>&quot;General&quot;</strong> tabu:
                <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                  <li>Hostname: <code>kif-rfid</code></li>
                  <li>Username: <code>pi</code>, postavi lozinku</li>
                  <li>WiFi: SSID + password</li>
                  <li>Locale: <code>Europe/Zagreb</code></li>
                </ul>
              </li>
              <li>U <strong>&quot;Services&quot;</strong> tabu — uključi SSH</li>
              <li>U <strong>&quot;Options&quot;</strong> tabu — uključi <em>&quot;Run script after first boot&quot;</em> i zalijepi <strong className="text-emerald-700 dark:text-emerald-300">snippet s produkcijskim tokenom</strong> (preporučeno):</li>
            </ol>

            {/* PRODUCTION snippet (with embedded token) */}
            <div className="relative">
              <pre className="bg-muted p-2 rounded font-mono text-[10px] overflow-x-auto pr-16 border-l-4 border-emerald-500" data-testid="bootstrap-snippet-prod">{prodSnippet}</pre>
              <Button
                size="sm"
                className="absolute top-1 right-1 h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={!readerToken}
                onClick={() => {
                  navigator.clipboard.writeText(prodSnippet);
                  toast.success("Kopiran snippet s produkcijskim tokenom — paste-aj u Pi Imager");
                }}
                data-testid="bootstrap-copy-prod"
              >
                Kopiraj s tokenom ✓
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"/>
              Pi će automatski koristiti produkcijski token — nije potrebno ručno editiranje
            </p>

            {/* Token reveal */}
            {readerToken && (
              <details className="text-[11px]" data-testid="token-reveal">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground underline">
                  Prikaži / sakrij token (za manualni backup)
                </summary>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-[10px] font-mono bg-muted/50 px-2 py-1 rounded select-all break-all flex-1">
                    {showToken ? readerToken : "•".repeat(Math.min(readerToken.length, 64))}
                  </code>
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] shrink-0" onClick={() => setShowToken(s => !s)}>
                    {showToken ? <EyeOff className="w-3 h-3"/> : <Eye className="w-3 h-3"/>}
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-7 text-[10px] shrink-0"
                    onClick={() => { navigator.clipboard.writeText(readerToken); toast.success("Token kopiran"); }}
                  >
                    Kopiraj samo token
                  </Button>
                </div>
              </details>
            )}

            {/* Fallback snippet without token */}
            <details className="text-[11px]" data-testid="bootstrap-no-token">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground underline">
                Alt: snippet bez ugrađenog tokena (paste-aš token kasnije ručno)
              </summary>
              <div className="relative mt-2">
                <pre className="bg-muted p-2 rounded font-mono text-[10px] overflow-x-auto pr-16" data-testid="bootstrap-snippet">{plainSnippet}</pre>
                <Button
                  size="sm" variant="outline" className="absolute top-1 right-1 h-7 text-[10px]"
                  onClick={() => { navigator.clipboard.writeText(plainSnippet); toast.success("Kopirano u clipboard"); }}
                  data-testid="bootstrap-copy"
                >
                  Kopiraj
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                Bez tokena će se koristiti default placeholder — daemon će dobiti 401 dok ručno ne urediš <code>~/kif-rfid/.env</code>.
              </p>
            </details>
            <p className="text-muted-foreground">
              8. <strong className="text-foreground">&quot;Save&quot; → &quot;Yes&quot; za primjenu Custom OS</strong> → <strong>&quot;Yes&quot; za flash</strong> → čekaj ~5 min flash + verifikaciju
            </p>
            <p className="text-muted-foreground">
              9. Izvadi SD, ubaci u Pi, spoji RFID čitač + ekran + napajanje → uključi → <strong className="text-foreground">prvi boot traje ~5-10 min</strong> (auto-install) → Pi se sam reboota → KIF kiosk se pojavi fullscreen
            </p>
            <div className="rounded bg-emerald-100/60 dark:bg-emerald-900/30 p-2 mt-2">
              <strong className="text-emerald-900 dark:text-emerald-100">✓ Po prvom boot-u sve radi:</strong>{" "}
              <span className="text-muted-foreground">RFID čita kartice → backend bilježi attendance → kiosk prikazuje &quot;doc. dr. sc. Ana Anić · PRIJAVA · 08:32:15&quot; + auto-reboot na grešku.</span>
            </div>
          </div>
        </details>

        {/* OPTION B: Manual installation via SCP */}
        <details className="rounded-md border bg-background/60 p-3" data-testid="rfid-pi-manual-option">
          <summary className="cursor-pointer font-semibold text-sm flex items-center gap-2">
            <FileDown className="w-4 h-4 text-muted-foreground"/>
            Opcija B: Manualna instalacija (već imaš Pi sa stockom OS-om)
          </summary>
          <div className="mt-3 space-y-3 text-xs">
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={downloadRfidBundle} disabled={rfidBusy || rfidFiles.length === 0} size="sm" data-testid="rfid-bundle-download">
                {rfidBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Package className="w-4 h-4 mr-2"/>}
                Preuzmi paket (.tar.gz)
              </Button>
              <span className="text-xs text-muted-foreground">{rfidFiles.length} datoteke</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {rfidFiles.map(f => (
                <button
                  key={f.name} type="button" onClick={() => downloadRfidFile(f.name)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border bg-background hover:bg-secondary/40 text-left transition"
                  data-testid={`rfid-file-${f.name}`}
                >
                  <FileDown className="w-3 h-3 text-muted-foreground shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono truncate">{f.name}</div>
                    <div className="text-[10px] text-muted-foreground">{Math.round(f.size_bytes / 102.4) / 10} KB</div>
                  </div>
                </button>
              ))}
            </div>
            <pre className="bg-muted/50 p-2 rounded font-mono text-[10px] overflow-x-auto">
{`# Na laptopu — prebaci na Pi
scp ~/Downloads/kif-rfid-pi.tar.gz pi@<IP-PI-A>:~/

# SSH na Pi
ssh pi@<IP-PI-A>
tar -xzf kif-rfid-pi.tar.gz && cd kif-rfid
sudo bash firstboot.sh   # auto-install + reboot`}
            </pre>
          </div>
        </details>

        <div className="rounded-md border bg-background/60 p-3 text-xs">
          <strong className="text-foreground">Mapiranje kartica zaposlenika:</strong>{" "}
          <span className="text-muted-foreground">
            Nakon što Pi radi, idi u <a className="underline" href="/zaposlenici">/zaposlenici</a> → uredi zaposlenika → klikni <em>&quot;Skeniraj karticu&quot;</em> (30s prozor) → prinesi karticu Pi čitaču → UID se auto-popuni i spremi.
          </span>
        </div>

        <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-300/50 p-3 text-xs">
          <strong className="text-amber-800 dark:text-amber-200">Sigurnosna napomena:</strong>{" "}
          <span className="text-muted-foreground">
            Pi mora koristiti isti <code>ATTENDANCE_READER_TOKEN</code> kao backend (postavljen u <code>backend/.env</code>).
            U produkciji generiraj jak token: <code>python3 -c &quot;import secrets; print(secrets.token_urlsafe(48))&quot;</code>.
            Drži Pi u fizički zaštićenoj zoni — pristup uređaju = pristup tokenu.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
